<template>
    <div>
        <b-modal hide-footer no-close-on-backdrop size="xl" title="Kostennote bearbeiten"
            :visible="isModalOpen" @hide="hideReset()" @show="loadForm">
            <div class="record-fee-edit-modal">
                <csp-desktop-info-card
                    ref="feeInfoCard"
                    :cols="12"
                    :form-context="formContext"
                    icon="inticon-users"
                    title="Kostennote"
                    @cancel-form="reset"
                    @save="saveFeeFormData">
                    <csp-record-detail-fee-edit-basedata
                        :form-context="formContext"/>
                    <hr>
                    <h2>Zahlungen</h2>
                    <b-row v-for="subformIdentifier in formContext.form.getSubformIdentifiers()"
                        :key="subformIdentifier"
                        class="mt-3">
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="Art der Zahlung"
                            :options-loader="loadPaymentTypeOptions"
                            property="paymentType"
                            required
                            :subform-identifier="subformIdentifier"
                            type="autocomplete"
                            @change="handlePaymentTypeChange(subformIdentifier)"/>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="bezahlt am"
                            property="datePaid"
                            required
                            :subform-identifier="subformIdentifier"
                            type="date"/>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="Bemerkung"
                            property="description"
                            :subform-identifier="subformIdentifier"
                            type="text"/>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="Bruttobetrag"
                            property="amount"
                            required
                            :subform-identifier="subformIdentifier"
                            type="currency"/>
                    </b-row>
                    <b-row v-if="!transientPaymentAdded" class="mt-2 mb-5" no-gutters>
                        <b-button class="col-12" size="sm" variant="primary" @click="addPayment">
                            Zahlung hinzufügen
                        </b-button>
                    </b-row>
                </csp-desktop-info-card>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import FeeEditBasedata from '@desktop/components/record/fee/RecordFeeEditBasedata.vue';
    import FormDataMixin from '@desktop/components/mixins/FormDataMixin';
    import Container from '@services/Container';
    import FeePaymentTypeEnum from '@js/enums/damage/FeePaymentTypeEnum';

    const container = Container.getInstance();
    const recordRepository = container.getDesktopRecordRepository();

    export default {
        name: 'FeeEditModal',
        components: {
            'csp-record-detail-fee-edit-basedata': FeeEditBasedata,
        },
        mixins: [FormDataMixin],
        props: {
            feeId: {
                type: Number,
                default: null,
            },
            isModalOpen: {
                type: Boolean,
                default: false,
            },
            recordId: {
                type: Number,
                required: true,
            },
        },
        emits: ['close', 'updated'],
        data() {
            return {
                lastPaymentTypeId: [],
                transientPaymentAdded: false,
            };
        },
        methods: {
            amountOpen() {
                return this.formContext.form.getProperty('amountOpen');
            },
            loadForm() {
                const loadFunction = () => {
                    return recordRepository.loadFeeEditForm(this.feeId, this.recordId);
                };
                this.loadFormData(loadFunction);
            },
            paymentType(subformIdentifier) {
                return this.formContext.form.getSubform(subformIdentifier).getProperty('paymentType');
            },
            handlePaymentTypeChange(subformIdentifier) {
                const paymentType = this.paymentType(subformIdentifier);

                if (!this.formContext.form.getSubform(subformIdentifier).getProperty('isEntityNew')) {
                    return; // no change if payment is not in new creation
                }

                switch (paymentType) {
                    case FeePaymentTypeEnum.FINALPAYMENT:
                        this.formContext.form.getSubform(subformIdentifier).setProperty(
                            'amount',
                            this.amountOpen()
                        );
                        break;
                    default: {
                        const lastPaymentType = this.lastPaymentType(subformIdentifier);
                        const presetPaymentTypes = [
                            FeePaymentTypeEnum.FINALPAYMENT,
                        ];

                        if (presetPaymentTypes.includes(lastPaymentType)) {
                            // reset amount to 0 if value was set by a preset payment type
                            this.formContext.form.getSubform(subformIdentifier).setProperty('amount', 0);
                        }
                    }
                }

                // set new type as last type for next change
                this.lastPaymentTypeId[subformIdentifier] = paymentType;
            },
            hideReset() {
                this.$refs.feeInfoCard.cancelFormData();
            },
            lastPaymentType(subformIdentifier) {
                return this.lastPaymentTypeId[subformIdentifier] || null;
            },
            reset() {
                this.transientPaymentAdded = false;
                this.formContext.isLoaded = false;
                this.$emit('close');
            },
            saveFeeFormData(formContext) {
                if (!this.shouldSaveFormData(formContext)) {
                    return;
                }

                const saveFunction = (objectId, postData) => {
                    return recordRepository.saveFeeWithPayments(this.feeId, this.recordId, postData);
                };
                this.saveFormData(formContext, saveFunction, false).then(() => {
                    this.$emit('updated');
                    this.reset();
                });
            },
            shouldSaveFormData(formContext) {
                const amountOpen = this.calculateAmountOpen(formContext);

                if (amountOpen >= 0) {
                    return true;
                }

                // current form values would result in overpayment, confirm with the user that this is acceptable
                return confirm(
                    'Bei der Eingabe der Beträge liegt eine Überzahlung vor.\n'
                        + 'Bitte prüfen Sie ob die Daten korrekt sind.\n'
                        + 'Wenden Sie sich bei Rückfragen gerne an unsere Hotline unter +49 (0) 351 320 446-66'
                        + ' oder den für Sie zuständigen Gebietsleiter bei autoclaim.'
                );
            },
            calculateAmountOpen(formContext) {
                const form = formContext.form;

                // calculate open amount before payments
                let amountOpen = (form.getDataOfProperty('amount') ?? 0)
                    - (form.getDataOfProperty('amountAdjustmentGross') ?? 0);

                // deduct payments from open amount
                Object.entries(form.getData()).forEach(([key, value]) => {
                    if (!key.startsWith('feePayment-')) {
                        // ignore form data that are not payment subforms
                        return;
                    }

                    amountOpen -= value.data.attributes.amount ?? 0;
                });

                return amountOpen;
            },
            loadPaymentTypeOptions() {
                return recordRepository.loadLegacySelectOptions('vehicleDamageRecordFeePaymentTypes');
            },
            addPayment() {
                const loadFunction = (objectId, postData) => {
                    return recordRepository.addFeePayment(this.feeId, this.recordId, postData);
                };
                this.transientPaymentAdded = true;
                this.reloadFormData(this.formContext, loadFunction);
            },
        },
    }
</script>
